/* eslint-disable no-param-reassign */
/* eslint-disable dot-notation */
import SafeGet from 'lodash.get';
import { capi } from '../../client';

export default {
  namespaced: true,

  state: {
    chatHost: window?.AppData?.chatHost,
    chatAvailability: false,
  },

  getters: {
    chatLocale: (state, getters, rootState) => {
      // The default chat locale will always be the locale of the SC, unless overridden in
      // Contentful.
      const defaultChatLocale = rootState.locale;
      const isChatOverrideLocaleEnabled = SafeGet(
        rootState.translations,
        'scContactSection.scContactSection.contactChannels.scChatSupportChannel.localeOverride.scChatOverrideLocale.chatOverrideLocaleEnabled',
        false,
      );
      let chatLocale = defaultChatLocale;

      if (isChatOverrideLocaleEnabled) {
        chatLocale = SafeGet(
          rootState.translations,
          'scContactSection.scContactSection.contactChannels.scChatSupportChannel.localeOverride.scChatOverrideLocale.chatOverrideLocale',
          defaultChatLocale,
        );
      }

      if (!chatLocale || chatLocale.length === 0) {
        chatLocale = defaultChatLocale;
      }

      return chatLocale.replace(/-/, '_');
    },

    chatHost: (state) => state.chatHost,

    /**
     * Get chat availability message
     * @returns {boolean} false if no message is available for the chat status,
     * {string} message if a message is available
     */
    chatAvailabilityMessage: (state, getters, rootState, rootGetters) => {
      const availability = state.chatAvailability;
      const queueEnabled = getters.isChatQueueingEnabled;
      let message = false;

      if (availability?.availabilityStatus) {
        if (
          (availability.availabilityStatus === 'NO_AGENTS_AVAILABLE' && !availability.isChatAvailable)
          || (availability.availabilityStatus === 'AVAILABLE' && !queueEnabled)
          || (availability.availabilityStatus === 'THRESHOLD_REACHED')
        ) {
          message = SafeGet(
            rootState.translations,
            'scContactSection.scContactSection.availabilityMessages.scContactAvailabilityBusy.copy',
            'Sorry, all our online chat specialists are busy assisting other customers at the moment.',
          );
        } else if (
          availability.availabilityStatus === 'OUT_OF_BUSINESS_HOURS'
          || availability.availabilityStatus === 'CLOSED'
        ) {
          message = SafeGet(
            rootState.translations,
            'scContactSection.scContactSection.availabilityMessages.scContactAvailabilityClosed.copy',
            'Sorry, we\'d like to help you with your device but our online chat is closed right now.',
          );
        }
      // eslint-disable-next-line dot-notation
      } else if (rootGetters['product']) {
        message = SafeGet(
          rootState.translations,
          'scContactSection.scContactSection.availabilityMessages.scContactAvailabilityNotSupported.copy',
          'Sorry, we\'re unable to help you with your device at this time.',
        );
      } else {
        message = SafeGet(
          rootState.translations,
          'scContactSection.scContactSection.availabilityMessages.scContactAvailabilityNoProduct.copy',
          'Sorry, our online chat is only available for certain devices at the current time.',
        );
      }

      return message;
    },

    /**
     * Determines whether or not the chat is available
     * @returns {boolean} indicating whether or not the chat is available
     */
    chatAvailability: (state, getters) => {
      const { chatAvailability } = state;
      const queueEnabled = getters.isChatQueueingEnabled;

      if (
        SafeGet(chatAvailability, 'isChatAvailable')
          && SafeGet(chatAvailability, 'availabilityStatus')
          && (chatAvailability.availabilityStatus === 'AVAILABLE_IMMEDIATELY'
          || (chatAvailability.availabilityStatus === 'AVAILABLE' && queueEnabled))
      ) {
        return true;
      }

      return false;
    },

    isChatQueueingEnabled: (state, getters, rootState, rootGetters) => {
      const { componentizedTranslations } = rootGetters;
      return SafeGet(
        componentizedTranslations,
        'scContactSection.contactChannels[0].fields.queueEnableFlag',
        false,
      );
    },
  },

  mutations: {
    chatAvailabilityUpdate(state, availability) {
      state.chatAvailability = availability;
    },
  },

  actions: {
    async fetchChatAvailability({ rootGetters, commit }) {
      const params = {};
      const apiId = 'fetchChatAvailability';
      if (
        rootGetters[`isApiIdCallInProgress(${apiId})`]
        || !rootGetters['product']
        || rootGetters.isInternal
      ) return;

      commit('incrementApiCallsInProgress', apiId, { root: true });

      if (rootGetters['product']) {
        params.launchCode = rootGetters['product'].chatIdentifier;
        params.queues = rootGetters['product'].chatQueueName;
      }

      if (!params.launchCode && !params.queues) {
        commit('decrementApiCallsInProgress', apiId, { root: true });
        return;
      }

      try {
        const { data } = await capi.get('chatAvailability/', { params });

        commit('chatAvailabilityUpdate', data);
      } catch (e) {
        commit('chatAvailabilityUpdate', false);
      } finally {
        commit('decrementApiCallsInProgress', apiId, { root: true });
      }
    },
  },
};
